/**
 * Booking Flow Keyboard Builders
 *
 * Centralized keyboard builders for Telegram inline keyboards.
 * Encapsulates callback data format logic so renderers don't need
 * to know the token patterns.
 *
 * Callback Data Patterns (from F5):
 * - ap:{bookingId}:{a|d|back} - Approval actions
 * - dn:{bookingId}:{reasonCode} - Denial with reason
 */

import type { InlineKeyboardButton } from '@grammyjs/types';
import type { ButtonStyle } from './rich-message.js';

/**
 * in-bubble-buttons D4: a button may carry a style hint, set by its action where it
 * is built: success to approve or confirm, danger to deny, cancel, withdraw or
 * remove, primary for a message's single door into the app; everything else unset.
 */
export type StyledInlineKeyboardButton = InlineKeyboardButton & { style?: ButtonStyle };

/**
 * Inline keyboard structure matching Telegram's expected format.
 * This is a plain object, NOT grammY's InlineKeyboard class.
 */
export interface InlineKeyboard {
  inline_keyboard: StyledInlineKeyboardButton[][];
}

/**
 * Admin denial reasons: callback code → the label shown on the reason button,
 * in the admin's resolved prompt and in the client's denial message.
 */
export const DENIAL_REASON_LABELS = {
  conflict: 'Schedule Conflict',
  incomplete: 'Incomplete Requirements',
  capacity: 'Capacity Reached',
  other: 'Other',
} as const;

export type DenialReasonCode = keyof typeof DENIAL_REASON_LABELS;

/** A known reason code renders as its label; anything else is free text and renders as written. */
export function denialReasonLabel(reason: string): string {
  return Object.prototype.hasOwnProperty.call(DENIAL_REASON_LABELS, reason)
    ? DENIAL_REASON_LABELS[reason as DenialReasonCode]
    : reason;
}

/**
 * Centralized keyboard builders for booking flow.
 *
 * All methods return plain objects with `inline_keyboard` property
 * that can be passed directly to Telegram API or grammY's reply methods.
 */
export const BookingKeyboards = {
  /**
   * "View my booking" web_app doorbell (notification-audit copy pass). The single
   * working button that replaced the old inert calendar/reschedule/cancel rows
   * (card `booking-chat-buttons-inert`) — the BookingDetailPage carries change +
   * cancel + details in-app.
   *
   * @param url - The BookingDetailPage deep-link URL (`/booking-hub/booking/:id`)
   */
  viewMyBooking(url: string, label = 'View my booking'): InlineKeyboard {
    return {
      inline_keyboard: [
        [{ text: label, web_app: { url }, style: 'primary' }],
      ],
    };
  },

  /**
   * virtual-sessions-finish D10: "Join on Zoom" ("Join online" for Other) as a
   * plain URL button that opens the meeting outside Telegram. Telegram accepts
   * only http, https and tg links here, which is why every door stores http(s)
   * only. Rendered above "View my booking" when a message carries both.
   *
   * @param url - The resolved meeting link (http or https)
   * @param platformLabel - `virtualPlatformLabel` of the platform; null for Other
   */
  joinMeetingRow(url: string, platformLabel: string | null): StyledInlineKeyboardButton[] {
    return [{ text: platformLabel ? `Join on ${platformLabel}` : 'Join online', url }];
  },

  /** booking-directions-button: Google Maps directions to an in-person booking's place, opened outside Telegram. */
  directionsButton(url: string): StyledInlineKeyboardButton {
    return { text: 'Get directions', url };
  },

  // approvalActions (✅ Approve / ❌ Deny) retired 2026-10-02: the pending-approval
  // prompt offers Review only. Prompts delivered earlier still carry those
  // buttons; the admin bot keeps handling `ap:{id}:a` and `ap:{id}:d`.

  /**
   * Denial reason selection buttons (reached from a pre-2026-10-02 prompt's Deny).
   *
   * @param bookingId - The booking ID for callback data
   * @returns Keyboard with denial reason options and back button
   */
  denialReasons(bookingId: string): InlineKeyboard {
    return {
      inline_keyboard: [
        ...(Object.keys(DENIAL_REASON_LABELS) as DenialReasonCode[]).map((code) => [
          { text: DENIAL_REASON_LABELS[code], callback_data: `dn:${bookingId}:${code}`, style: 'danger' as const },
        ]),
        [{ text: '← Back', callback_data: `ap:${bookingId}:back` }],
      ],
    };
  },

} as const;
